import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useContext } from 'react';
import { getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { Icon, ChevronDownSVGpath } from '@skatteetaten/ds-icons';
import { AccordionContext } from './AccordionContext/index.esm.js';
import './AccordionItem/styles.css';


const getAccordionIconPositionDefault = ()=>'right';
const getAccordionBackgroundColorDefault = ()=>'none';
const getAccordionSizeDefault = ()=>'medium';

var styles = {
    "accordionItem": "AccordionItem-module_accordionItem__HBH7F",
    "tag": "AccordionItem-module_tag__FatCL",
    "header": "AccordionItem-module_header__6VcJ6",
    "icon": "AccordionItem-module_icon__2kyGJ",
    "chevron_open": "AccordionItem-module_chevron_open__1dcOe",
    "chevron_closed": "AccordionItem-module_chevron_closed__LW0cA",
    "chevron_medium": "AccordionItem-module_chevron_medium__1t7Wq",
    "chevron_large": "AccordionItem-module_chevron_large__CTu4Z",
    "header_iconLeft": "AccordionItem-module_header_iconLeft__IHYQN",
    "header_medium": "AccordionItem-module_header_medium__BrRD5",
    "header_large": "AccordionItem-module_header_large__C0s4E",
    "titleWrapper": "AccordionItem-module_titleWrapper__oRK4G",
    "iconWrapper": "AccordionItem-module_iconWrapper__4hWU7",
    "iconWrapper_medium": "AccordionItem-module_iconWrapper_medium__q3Fca",
    "iconWrapper_large": "AccordionItem-module_iconWrapper_large__9uNY3",
    "title": "AccordionItem-module_title__dr-1l",
    "title_medium": "AccordionItem-module_title_medium__JDRwR",
    "title_large": "AccordionItem-module_title_large__8M-NJ",
    "subtitle": "AccordionItem-module_subtitle__9xUe8",
    "content": "AccordionItem-module_content__eAS4C",
    "content_collapsed": "AccordionItem-module_content_collapsed__SIWEh",
    "content_smallIndented": "AccordionItem-module_content_smallIndented__WpuNc",
    "content_mediumIndented": "AccordionItem-module_content_mediumIndented__V1F7y",
    "content_largeIndented": "AccordionItem-module_content_largeIndented__BzjcK",
    "content_medium": "AccordionItem-module_content_medium__qn-4p",
    "content_large": "AccordionItem-module_content_large__8htMS"
};

const AccordionItem = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, classNames, title, subtitle, titleAs, isDefaultExpanded, isExpanded: isExpandedExternal, svgPath, onClick, children }, ref)=>{
    const [isExpandedInternal, setIsExpandedInternal] = useState(isDefaultExpanded ?? false);
    const { size = getAccordionSizeDefault(), iconPosition } = useContext(AccordionContext);
    const handleClick = (e)=>{
        onClick?.(e);
        setIsExpandedInternal(!isExpanded);
    };
    const isExpanded = isExpandedExternal !== undefined ? isExpandedExternal : isExpandedInternal;
    const shouldDisplayCustomIcon = iconPosition === 'right' && !!svgPath;
    const shouldIndentContent = iconPosition === 'left' || shouldDisplayCustomIcon;
    const iconSize = size === 'small' ? 'medium' : 'large';
    const iconWrapperClassNames = `${styles.iconWrapper} ${size !== 'small' ? styles[`iconWrapper_${size}`] : ''}`.trim();
    const chevronClassNames = `${styles.icon} ${size !== 'small' ? styles[`chevron_${size}`] : ''} ${isExpanded ? styles.chevron_open : styles.chevron_closed} `.trim();
    const headerClassNames = `${styles.header} ${iconPosition === 'left' ? styles.header_iconLeft : ''} ${size !== 'small' ? styles[`header_${size}`] : ''} ${className} ${classNames?.container ?? ''}`.trim();
    const titleClassNames = `${styles.title} ${size !== 'small' ? styles[`title_${size}`] : ''} ${classNames?.title ?? ''}`.trim();
    const subtitleClassNames = `${styles.subtitle} ${classNames?.subtitle ?? ''}`.trim();
    const contentClassNames = `${styles.content} ${size !== 'small' ? styles[`content_${size}`] : ''} ${shouldIndentContent ? styles[`content_${size}Indented`] : ''} ${classNames?.content ?? ''}`.trim();
    const Tag = titleAs ?? 'div';
    return /*#__PURE__*/ jsxs("div", {
        className: styles.accordionItem,
        children: [
            /*#__PURE__*/ jsx(Tag, {
                className: styles.tag,
                children: /*#__PURE__*/ jsxs("button", {
                    ref: ref,
                    id: id,
                    className: headerClassNames,
                    lang: lang,
                    "data-testid": dataTestId,
                    "aria-expanded": isExpanded,
                    type: 'button',
                    onClick: handleClick,
                    children: [
                        svgPath && iconPosition !== 'left' && /*#__PURE__*/ jsx("div", {
                            className: iconWrapperClassNames,
                            children: /*#__PURE__*/ jsx(Icon, {
                                svgPath: svgPath,
                                size: iconSize,
                                className: styles.icon
                            })
                        }),
                        /*#__PURE__*/ jsxs("div", {
                            className: styles.titleWrapper,
                            children: [
                                /*#__PURE__*/ jsx("div", {
                                    className: titleClassNames,
                                    children: title
                                }),
                                subtitle && /*#__PURE__*/ jsx("p", {
                                    className: subtitleClassNames,
                                    children: subtitle
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx("div", {
                            className: iconWrapperClassNames,
                            children: /*#__PURE__*/ jsx(Icon, {
                                svgPath: ChevronDownSVGpath,
                                size: iconSize,
                                className: chevronClassNames
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: isExpanded ? contentClassNames : styles.content_collapsed,
                children: children
            })
        ]
    });
});
AccordionItem.displayName = 'AccordionItem';

export { AccordionItem as A, getAccordionBackgroundColorDefault as a, getAccordionSizeDefault as b, getAccordionIconPositionDefault as g };
